import { useContext, useEffect, useState } from 'react';
import * as Yup from 'yup';
import useFetchEmployees from 'components/domain/employee/useFetchEmployees';
import { getCurrencyOptions } from 'utils/analytics/analyticsUtils';
import useFetchAllProperties from 'components/domain/property/useFetchAllProperties';
import useAppQuery from '../../../hooks/useAppQuery';
import API from '../../../services/API';
import useAppUser from '../../../hooks/useAppUser';
import { GetAllLeadChannelsResponseTO } from '../../../models/Leads';
import AnalyticsContext from '../AnalyticsContext';
import {
  ChartFilter,
  ChartType,
  FinancialPerformanceMetric,
  PropertyPerformanceMetric,
  TeamPerformanceMetric,
} from './charts/Charts.types';
import {
  propertyAnalyticsConditionGraphQLQuery,
  propertyAnalyticsGraphQLQuery,
} from './Analytics.utils';
import { AnalyticsBasicProperty } from './types/Analytics.types';

const useAnalyticsInit = () => {
  const { isEmployee, isOwner, agency } = useAppUser();
  const { setLeadChannels, setAllProperties, setEmployees } =
    useContext(AnalyticsContext);
  const [defaultCurrency, setDefaultCurrency] = useState<String>();
  const { employees, isFetching } = useFetchEmployees();
  const { chartsFilterLocalStorageKey } = useContext(AnalyticsContext);
  const { allProperties: properties, isFetching: isPropertiesLoading } =
    useFetchAllProperties({
      // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      enabled: isEmployee || isOwner,
      conditions: propertyAnalyticsConditionGraphQLQuery,
      fields: propertyAnalyticsGraphQLQuery,
    });

  const storedFilter = () => {
    const filter = JSON.parse(
      // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      localStorage.getItem(chartsFilterLocalStorageKey),
    );

    if (!filter?.metric) {
      return null;
    }

    if (
      filter.metric === FinancialPerformanceMetric.RENTAL_REVENUE &&
      isOwner
    ) {
      return {
        ...filter,
        metric: FinancialPerformanceMetric.OWNER_REVENUE,
      };
    }

    if (filter.metric === PropertyPerformanceMetric.RENTAL_REVENUE && isOwner) {
      return {
        ...filter,
        metric: PropertyPerformanceMetric.OWNER_REVENUE,
      };
    }
    return filter;
  };

  const chartFormSchema = () => ({
    type: Yup.mixed<ChartType>().oneOf(Object.values(ChartType)).required(),
    metric: Yup.mixed<
      | FinancialPerformanceMetric
      | PropertyPerformanceMetric
      | TeamPerformanceMetric
    >().required(),
    offset: Yup.number().required(),
    limit: Yup.number().required(),
  });
  const chartFormDefaultValues: ChartFilter = storedFilter() || {
    type: ChartType.FINANCIAL_PERFORMANCE,
    metric: isOwner
      ? FinancialPerformanceMetric.OWNER_REVENUE
      : FinancialPerformanceMetric.RENTAL_REVENUE,
    offset: 0,
    limit: 25,
  };

  const { data: leadChannels, isLoading: isLeadChannelsLoading } = useAppQuery(
    ['leadChannels'],
    async () => {
      const response = await API.get<GetAllLeadChannelsResponseTO>(
        '/api/internal/leads-channels',
      );
      return response.data.leadChannels;
    },
    { enabled: isEmployee || isOwner },
  );

  useEffect(() => {
    if (leadChannels) {
      setLeadChannels(leadChannels);
    }

    if (properties) {
      setAllProperties(properties as unknown as AnalyticsBasicProperty[]);

      setDefaultCurrency(
        // prettier-ignore
        getCurrencyOptions(properties as unknown as AnalyticsBasicProperty[])[0]
        // @ts-expect-error TS2339 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          ?.value || agency.currency,
      );
    }

    if (employees) {
      setEmployees(employees);
    }
  }, [leadChannels, properties, employees]);

  return {
    isLoading:
      (isEmployee || isOwner) &&
      (isLeadChannelsLoading || isPropertiesLoading || isFetching),
    chartFormSchema,
    chartFormDefaultValues,
    defaultCurrency,
  };
};

export default useAnalyticsInit;
